/**
 * Error Reporting Utilities
 *
 * Centralized error reporting to external monitoring services (Bugsnag).
 * Provides graceful degradation when monitoring services are unavailable.
 */

/**
 * Reports an error to Bugsnag with structured metadata
 *
 * Gracefully handles cases where Bugsnag is not available.
 * In development, falls back to console for better visibility.
 * Ensures message is converted to Error object for proper stack traces.
 *
 * @param {string|Error} message - Error message or Error object
 * @param {string} severity - Severity level ('error', 'warning', 'info')
 * @param {string} context - Error context/category for filtering in Bugsnag dashboard
 * @param {Object} metadata - Additional metadata organized in groups (http-status, retry-info, form-info, etc.)
 * @param {boolean} fallbackToConsole - Log to console if Bugsnag unavailable (default: true)
 *
 * @example
 * reportToBugsnag(
 *   'Form submission failed',
 *   'error',
 *   'form-submission-error',
 *   {
 *     'http-status': { code: 500, text: 'Internal Server Error' },
 *     'retry-info': { attempt: 3, maxRetries: 3 },
 *     'form-info': { formId: 'contact-form' }
 *   }
 * );
 */
export const reportToBugsnag = (message, severity, context, metadata = {}, fallbackToConsole = true) => {
    if (typeof Bugsnag === 'undefined' || typeof Bugsnag.notify !== 'function') {
        if (fallbackToConsole) {
            const errorPrefix = `[Bugsnag Unavailable][${severity}][${context}]`;
            const error = message instanceof Error ? message : new Error(message);

            console.groupCollapsed(`${errorPrefix} ${error.message}`);
            console.error(error);
            if (Object.keys(metadata).length > 0) {
                console.log('Metadata:', metadata);
            }
            console.groupEnd();
        }
        return;
    }

    // Ensure message is an Error object for proper stack traces
    const error = message instanceof Error ? message : new Error(message);

    Bugsnag.notify(error, function (event) {
        event.severity = severity;
        event.context = context;

        // Add metadata groups
        Object.keys(metadata).forEach(key => {
            event.addMetadata(key, metadata[key]);
        });
    });
};

/**
 * Adds a breadcrumb to Bugsnag for debugging context
 *
 * Gracefully handles cases where Bugsnag is not available.
 * Breadcrumbs provide execution history leading up to errors.
 *
 * @param {string} message - Breadcrumb message
 * @param {Object} metadata - Additional metadata for breadcrumb
 * @param {string} type - Breadcrumb type ('navigation', 'user', 'state', 'error', 'manual', etc.)
 *
 * @example
 * addBreadcrumb('Form step navigation', {
 *   stepFrom: 1,
 *   stepTo: 2,
 *   formId: 'multi-step-form'
 * }, 'user');
 */
export const addBreadcrumb = (message, metadata = {}, type = 'manual') => {
    if (typeof Bugsnag === 'undefined') {
        return;
    }

    Bugsnag.leaveBreadcrumb(message, metadata, type);
};

export default { reportToBugsnag, addBreadcrumb };
